import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import dayjs from 'dayjs';
import { CATEGORY_CHOICES, CURRENCY_CHOICES, STATUS_CHOICES } from '../../utils/incomeUtils';

const IncomeForm = ({ open, mode, formData, formErrors, moneyAccounts, onClose, onSubmit, onInputChange }) => {
  const [selectedAccount, setSelectedAccount] = useState(null);

  useEffect(() => {
    if (formData.money_account && moneyAccounts) {
      const account = moneyAccounts.find(acc => acc.id.toString() === formData.money_account.toString());
      setSelectedAccount(account);
    } else {
      setSelectedAccount(null);
    }
  }, [formData.money_account, moneyAccounts]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'add' ? 'New Income' : mode === 'edit' ? 'Edit Income' : 'Income Details'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => onInputChange('name', e.target.value)}
              disabled={mode === 'view'}
              error={!!formErrors.name}
              helperText={formErrors.name}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!formErrors.category}>
              <InputLabel required>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => onInputChange('category', e.target.value)}
                disabled={mode === 'view'}
              >
                {CATEGORY_CHOICES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.category && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {formErrors.category}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!formErrors.currency}>
              <InputLabel required>Currency</InputLabel>
              <Select
                value={formData.currency}
                label="Currency"
                onChange={(e) => onInputChange('currency', e.target.value)}
                disabled={mode === 'view'}
              >
                {CURRENCY_CHOICES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.currency && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {formErrors.currency}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={(e) =>
                onInputChange('amount', e.target.value === '' ? '' : String(Math.max(0, parseFloat(e.target.value) || 0)))
              }
              disabled={mode === 'view'}
              error={!!formErrors.amount}
              helperText={formErrors.amount}
              required
              InputProps={{
                startAdornment: <AccountBalanceIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                inputProps: { min: 0 },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!formErrors.status}>
              <InputLabel required>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => onInputChange('status', e.target.value)}
                disabled={mode === 'view'}
              >
                {STATUS_CHOICES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.status && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                  {formErrors.status}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Money Account</InputLabel>
              <Select
                value={formData.money_account || ''}
                label="Money Account"
                onChange={(e) => onInputChange('money_account', e.target.value)}
                disabled={mode === 'view'}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {moneyAccounts?.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name} ({account.account_type}) - {account.currency} {Number(account.balance || 0).toLocaleString()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedAccount && (
              <Box sx={{ mt: 1, p: 1, bgcolor: 'info.lighter', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalanceWalletIcon fontSize="small" />
                  Current Balance: {selectedAccount.currency} {Number(selectedAccount.balance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date *"
                value={formData.date ? dayjs(formData.date) : null}
                onChange={(newValue) => {
                  const formatted = newValue ? dayjs(newValue).format('YYYY-MM-DD') : '';
                  onInputChange('date', formatted);
                }}
                disabled={mode === 'view'}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                    error: !!formErrors.date,
                    helperText: formErrors.date,
                    required: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Remarks"
              multiline
              rows={3}
              value={formData.remarks}
              onChange={(e) => onInputChange('remarks', e.target.value)}
              disabled={mode === 'view'}
              error={!!formErrors.remarks}
              helperText={formErrors.remarks}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {mode !== 'view' && (
          <Button onClick={onSubmit} variant="contained">
            {mode === 'add' ? 'Create' : 'Update'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

IncomeForm.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['add', 'edit', 'view']).isRequired,
  formData: PropTypes.shape({
    name: PropTypes.string.isRequired,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    category: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    remarks: PropTypes.string,
    money_account: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  formErrors: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
};

IncomeForm.defaultProps = {
  formErrors: {},
};

export default IncomeForm;